import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { currentUserKey } from 'hooks/use-current-user';
import { useStartZoomLink, useUnlinkZoom, useZoomLink } from 'hooks/use-zoom-link';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { toast } from 'sonner';

interface ZoomSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ZoomSettingsDialog ({ open, onOpenChange }: ZoomSettingsDialogProps) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useZoomLink({ enabled: open, pollWhilePending: true });
  const startLink = useStartZoomLink();
  const unlink = useUnlinkZoom();

  // Once the Zoom-side code lands, the poll flips `linked`; tell the user
  // and refresh the cached user so the menu label updates.
  const wasLinked = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (data === undefined) return;
    if (wasLinked.current === false && data.linked) {
      toast.success('Zoom linked');
      queryClient.invalidateQueries({ queryKey: currentUserKey });
    }
    wasLinked.current = data.linked;
  }, [data, queryClient]);

  const handleStart = async () => {
    try {
      await startLink.mutateAsync();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleUnlink = async () => {
    try {
      await unlink.mutateAsync();
      toast.success('Zoom unlinked');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const pending = !!data && !data.linked && !!data.link_code;
  const botName = data?.bot_name ?? 'HiLo Analytics';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Zoom Team Chat</DialogTitle>
          <DialogDescription>
            Chat with the HiLo agent from Zoom. Messages you send to the{' '}
            <span className="font-medium">{botName}</span> app in Zoom Team Chat are answered in a
            chat on your account.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {isError && <div className="text-sm text-destructive">Failed to load Zoom status.</div>}

        {data?.linked && (
          <div className="flex flex-col gap-3 text-sm">
            <div>
              Linked Zoom user: <span className="font-mono text-xs">{data.zoom_user_jid}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              A conversation closes after an hour of quiet. Send <code>/new</code> to start a
              fresh one at any time and <code>/continue</code> to pick up a closed one.
            </p>
          </div>
        )}

        {data && !data.linked && !pending && (
          <p className="text-sm">
            Get a one-time code, then send it to the <span className="font-medium">{botName}</span>{' '}
            app in Zoom (Team Chat → Apps → {botName}). The code expires after 15 minutes.
          </p>
        )}

        {pending && (
          <div className="flex flex-col gap-3 text-sm">
            <div className="text-xs text-muted-foreground">
              In Zoom, open Team Chat → Apps → <span className="font-medium">{botName}</span> and
              send this code as a message:
            </div>
            <div className="font-mono text-3xl tracking-[0.3em] text-center py-2 rounded-md border">
              {data.link_code}
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Waiting for your message…
            </div>
          </div>
        )}

        <DialogFooter>
          {data?.linked ? (
            <Button variant="outline" onClick={handleUnlink} disabled={unlink.isPending}>
              {unlink.isPending ? 'Unlinking…' : 'Unlink'}
            </Button>
          ) : (
            <Button onClick={handleStart} disabled={startLink.isPending || isLoading}>
              {startLink.isPending ? 'Working…' : pending ? 'New code' : 'Get link code'}
            </Button>
          )}
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
